import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ThemeContext = createContext(null);

const OVERLAY_COLOR = {
    dark: "#070b14",
    light: "#f7f4ec",
};

export function ThemeProvider({ children }) {
    const [theme, setTheme] = useState(() => {
        try {
            const saved = window.localStorage.getItem("omar-portfolio-theme");
            if (saved === "dark" || saved === "light") return saved;
            const prefersLight = window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches;
            return prefersLight ? "light" : "dark";
        } catch {
            return "dark";
        }
    });
    const [transitioning, setTransitioning] = useState(false);
    const [overlayColor, setOverlayColor] = useState(OVERLAY_COLOR.dark);
    const timers = useRef([]);

    useEffect(() => {
        document.documentElement.setAttribute("data-theme", theme);
        try {
            window.localStorage.setItem("omar-portfolio-theme", theme);
        } catch {
            /* ignore storage errors */
        }
    }, [theme]);

    useEffect(() => () => timers.current.forEach((id) => clearTimeout(id)), []);

    const toggleTheme = useCallback(() => {
        setTheme((prev) => {
            const next = prev === "dark" ? "light" : "dark";
            setOverlayColor(OVERLAY_COLOR[next]);
            setTransitioning(true);

            const switchTimer = setTimeout(() => {
                document.documentElement.setAttribute("data-theme", next);
            }, 260);
            const endTimer = setTimeout(() => setTransitioning(false), 720);
            timers.current.push(switchTimer, endTimer);

            return next;
        });
    }, []);

    const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);

    return (
        <ThemeContext.Provider value={value}>
            {children}
            <AnimatePresence>
                {transitioning && (
                    <motion.div
                        key="theme-transition"
                        className="theme-transition-overlay"
                        style={{ background: overlayColor }}
                        initial={{ opacity: 0, x: "35%" }}
                        animate={{ opacity: [0, 1, 1, 0], x: ["35%", "0%", "0%", "-35%"] }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.72, times: [0, 0.34, 0.56, 1], ease: [0.65, 0, 0.35, 1] }}
                    />
                )}
            </AnimatePresence>
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
    return ctx;
}
